import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
    AushubParameter, Cluster, ClusterArt, Expositionsstufe, Expositionsweg, Rasterfeld,
    Schutzklasse, Taetigkeit,
} from '../types';
import { klassenLabel } from '../einstufung';
import type { ClusterEinstufung } from '../einstufung';
import type { Vorschlag } from '../schutzklassen';
import {
    AuswahlFeld, Leerzeile, MarkenFeld, NeuZeile, TextFeld, Untertitel, ZahlFeld, ZeilenKarte,
} from './bausteine';
import { leseAushub, mitAushub } from '../skizzen/aushub';

/**
 * Arbeitsbereiche und die Taetigkeiten darin.
 *
 * Beide zusammen in einer Ansicht, weil sie zusammen gedacht werden muessen:
 * Anhang 3 Nr. 4 verlangt die Einteilung in Arbeitsbereiche, Nr. 5 die
 * Beurteilung der Taetigkeiten DARIN. Wer sie trennt, legt Bereiche an und
 * vergisst die Taetigkeiten — genau der Sprung vom Stoffspektrum zu den
 * Schutzmassnahmen, den die BG BAU als haeufigsten Mangel benennt.
 */

const ART_OPTIONEN: { wert: ClusterArt; label: string }[] = [
    { wert: 'regel', label: 'Aushub / Rückbau' },
    { wert: 'vorbehalt', label: 'Analytik-Vorbehalt' },
    { wert: 'nebenarbeit', label: 'Nebenarbeit (Transport, Lager, Wasserhaltung …)' },
    { wert: 'abgegrenzt', label: 'nicht A+S-pflichtig (ausdrücklich abgegrenzt)' },
];

const STUFEN: { wert: Expositionsstufe; label: string }[] = [
    { wert: 'keine', label: 'keine' },
    { wert: 'gering', label: 'gering' },
    { wert: 'mittel', label: 'mittel' },
    { wert: 'hoch', label: 'hoch' },
];

const WEGE: Expositionsweg[] = ['inhalativ', 'dermal', 'oral'];

interface Props {
    cluster: Cluster[];
    taetigkeiten: Taetigkeit[];
    felder: Rasterfeld[];
    schutzklassen: Schutzklasse[];
    lage: Map<string, { einstufung: ClusterEinstufung; vorschlag: Vorschlag }>;
    gesperrt: boolean;
    bereichAnlegen: (kennung: string) => Promise<unknown>;
    bereichAendern: (id: string, u: Partial<Cluster>) => void;
    bereichLoeschen: (id: string) => void;
    taetigkeitAnlegen: (cluster_kennung: string, bezeichnung: string) => Promise<unknown>;
    taetigkeitAendern: (id: string, u: Partial<Taetigkeit>) => void;
    taetigkeitLoeschen: (id: string) => void;
}

export const BereicheEditor: React.FC<Props> = ({
    cluster, taetigkeiten, felder, schutzklassen, lage, gesperrt,
    bereichAnlegen, bereichAendern, bereichLoeschen,
    taetigkeitAnlegen, taetigkeitAendern, taetigkeitLoeschen,
}) => {
    const [offen, setOffen] = useState<Set<string>>(new Set());
    const umschalten = (id: string) => setOffen(s => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    const klassenOptionen = [...schutzklassen]
        .sort((a, b) => a.rang - b.rang)
        .map(k => ({ wert: k.key, label: `${k.key} — ${k.bezeichnung}` }));
    const feldNummern = felder.map(f => f.feld_nr);

    return (
        <>
            <NeuZeile
                platzhalter="Kennung, z. B. C"
                knopf="Bereich anlegen"
                gesperrt={gesperrt}
                breite="w-40"
                onAnlegen={bereichAnlegen}
            />

            {cluster.length === 0 ? (
                <Leerzeile text="Noch kein Arbeitsbereich angelegt." />
            ) : (
                <ul className="space-y-3">
                    {cluster.map(c => {
                        const l = lage.get(c.kennung);
                        const eigene = taetigkeiten.filter(t => t.cluster_kennung === c.kennung);
                        const auf = offen.has(c.id);
                        const abgegrenzt = c.art === 'abgegrenzt';

                        return (
                            <ZeilenKarte
                                key={c.id}
                                gesperrt={gesperrt}
                                onLoeschen={() => bereichLoeschen(c.id)}
                                loeschTitel={`Bereich ${c.kennung} löschen`}
                                kopf={
                                    <button
                                        type="button"
                                        onClick={() => umschalten(c.id)}
                                        className="flex items-center gap-2 text-left w-full"
                                    >
                                        {auf ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                            : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                        <span className="font-bold text-slate-800">
                                            {c.kennung} · {c.bezeichnung || 'ohne Bezeichnung'}
                                        </span>
                                        <span className="text-xs text-slate-500">
                                            {c.felder.length} Feld(er) · {eigene.length} Tätigkeit(en)
                                        </span>
                                        <span className="ml-auto px-2 py-0.5 rounded bg-slate-100 text-xs font-black">
                                            {abgegrenzt ? 'nicht A+S' : (c.schutzklasse_key || '—')}
                                        </span>
                                    </button>
                                }
                            >
                                {auf && (
                                    <>
                                        <div className="grid sm:grid-cols-3 gap-2 mb-3">
                                            <TextFeld
                                                label="Bezeichnung"
                                                wert={c.bezeichnung}
                                                platzhalter="Widerlager Süd"
                                                gesperrt={gesperrt}
                                                onSpeichern={v => bereichAendern(c.id, { bezeichnung: v })}
                                            />
                                            <TextFeld
                                                label="Baumaßnahme"
                                                wert={c.baumassnahme}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => bereichAendern(c.id, { baumassnahme: v })}
                                            />
                                            <AuswahlFeld
                                                label="Art des Bereichs"
                                                wert={c.art}
                                                optionen={ART_OPTIONEN}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => bereichAendern(c.id, { art: v })}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <MarkenFeld
                                                label="Rasterfelder dieses Bereichs"
                                                werte={c.felder}
                                                vorschlaege={feldNummern}
                                                gesperrt={gesperrt}
                                                platzhalter="Feld-Nr."
                                                onSpeichern={v => bereichAendern(c.id, { felder: v })}
                                            />
                                            {l && (
                                                <p className="text-[11px] text-slate-600 mt-1.5">
                                                    Einstufung aus den Proben:{' '}
                                                    <b>{klassenLabel(l.einstufung.klasse, l.einstufung.vorbehalt)}</b>
                                                    {l.einstufung.massgebliches_feld
                                                        && <> (maßgeblich Feld {l.einstufung.massgebliches_feld})</>}
                                                    {l.einstufung.felder_ohne_proben.length > 0 && (
                                                        <span className="text-amber-700">
                                                            {' '}· ohne Proben: {l.einstufung.felder_ohne_proben.join(', ')}
                                                        </span>
                                                    )}
                                                    {/* Steht neben der Einstufung und nicht nur in der
                                                        Befundliste: hier daneben liegt der Knopf, der
                                                        den Vorschlag mit einem Klick uebernimmt. */}
                                                    {l.einstufung.felder_mit_proben_ohne_einstufung.length > 0 && (
                                                        <span className="text-amber-700">
                                                            {' '}· Probe ohne Einstufung in: {l.einstufung.felder_mit_proben_ohne_einstufung.join(', ')}
                                                        </span>
                                                    )}
                                                </p>
                                            )}
                                        </div>

                                        {!abgegrenzt && (
                                            <div className="grid sm:grid-cols-2 gap-2 mb-3">
                                                <div>
                                                    <AuswahlFeld
                                                        label="Schutzklasse"
                                                        wert={c.schutzklasse_key}
                                                        optionen={klassenOptionen}
                                                        gesperrt={gesperrt}
                                                        onSpeichern={v => bereichAendern(c.id, { schutzklasse_key: v })}
                                                    />
                                                    {l?.vorschlag.key && l.vorschlag.key !== c.schutzklasse_key && (
                                                        <p className="text-[11px] text-slate-600 mt-0.5">
                                                            Vorschlag <b>{l.vorschlag.key}</b>: {l.vorschlag.begruendung}
                                                            {!gesperrt && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => bereichAendern(c.id, {
                                                                        schutzklasse_key: l.vorschlag.key,
                                                                        schutzklasse_vorschlag: l.vorschlag.key,
                                                                    })}
                                                                    className="ml-1 font-bold text-stone-700 underline"
                                                                >
                                                                    übernehmen
                                                                </button>
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                                <TextFeld
                                                    label="Begründung, falls niedriger als vorgeschlagen"
                                                    wert={c.schutzklasse_begruendung}
                                                    mehrzeilig
                                                    gesperrt={gesperrt}
                                                    hinweis="Pflicht bei Abweichung nach unten — TRGS 524 Nr. 4.1 Abs. 15."
                                                    onSpeichern={v => bereichAendern(c.id, { schutzklasse_begruendung: v })}
                                                />
                                            </div>
                                        )}

                                        <div className="grid sm:grid-cols-3 gap-2 mb-3">
                                            <TextFeld
                                                label="Leitparameter"
                                                wert={c.leitparameter}
                                                platzhalter="PAK"
                                                gesperrt={gesperrt}
                                                onSpeichern={v => bereichAendern(c.id, { leitparameter: v })}
                                            />
                                            <TextFeld
                                                label="Umgebung"
                                                wert={String((c.umgebung as { temperatur?: string })?.temperatur ?? '')}
                                                platzhalter="Außenbereich, Sommer, offene Grube"
                                                gesperrt={gesperrt}
                                                hinweis="Geht in die Tragezeit ein."
                                                onSpeichern={v => bereichAendern(c.id, {
                                                    umgebung: { ...(c.umgebung ?? {}), temperatur: v },
                                                })}
                                            />
                                            <TextFeld
                                                label="Besonderheit"
                                                wert={c.besonderheit}
                                                gesperrt={gesperrt}
                                                onSpeichern={v => bereichAendern(c.id, { besonderheit: v })}
                                            />
                                        </div>

                                        <div className="grid sm:grid-cols-2 gap-2 mb-4">
                                            <TextFeld
                                                label="Befundlage"
                                                wert={c.befundlage}
                                                mehrzeilig
                                                gesperrt={gesperrt}
                                                onSpeichern={v => bereichAendern(c.id, { befundlage: v })}
                                            />
                                            <TextFeld
                                                label="Arbeitsweise / Aushubstrategie"
                                                wert={c.aushubstrategie}
                                                mehrzeilig
                                                gesperrt={gesperrt}
                                                onSpeichern={v => bereichAendern(c.id, { aushubstrategie: v })}
                                            />
                                        </div>

                                        {!abgegrenzt && (
                                            <AushubFelder
                                                umgebung={c.umgebung}
                                                klassenOptionen={klassenOptionen}
                                                gesperrt={gesperrt}
                                                onAendern={u => bereichAendern(c.id, {
                                                    umgebung: mitAushub(c.umgebung, u),
                                                })}
                                            />
                                        )}

                                        {!abgegrenzt && (
                                            <>
                                                <Untertitel
                                                    text="Tätigkeiten in diesem Bereich"
                                                    hinweis="Wer macht wann was wie, wie oft, wie lange — Anhang 3 Nr. 5."
                                                />
                                                <NeuZeile
                                                    platzhalter="Tätigkeit, z. B. Bodenaushub"
                                                    knopf="Tätigkeit"
                                                    gesperrt={gesperrt}
                                                    breite="w-56"
                                                    onAnlegen={t => taetigkeitAnlegen(c.kennung, t)}
                                                />
                                                {eigene.length === 0 ? (
                                                    <Leerzeile text="Für diesen Bereich ist keine Tätigkeit beurteilt." />
                                                ) : (
                                                    <ul className="space-y-2">
                                                        {eigene.map(t => (
                                                            <TaetigkeitsZeile
                                                                key={t.id}
                                                                taetigkeit={t}
                                                                klassenOptionen={klassenOptionen}
                                                                gesperrt={gesperrt}
                                                                onAendern={u => taetigkeitAendern(t.id, u)}
                                                                onLoeschen={() => taetigkeitLoeschen(t.id)}
                                                            />
                                                        ))}
                                                    </ul>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </ZeilenKarte>
                        );
                    })}
                </ul>
            )}
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Aushubparameter eines Arbeitsbereichs.
 *
 * ═══ WARUM DIESE WERTE HIER STEHEN UND NICHT AN DER ZEICHNUNG ═════════════
 * Der Schichtenschnitt wurde bis hierher mit festen Vorgaben gezeichnet —
 * fuer jedes Feld dieselbe Aushubsohle, derselbe Ueberaushub, nie eine
 * Trennlage. Das sah aus wie ein Erfassungsstand und war eine Voreinstellung.
 * Die Aushubstrategie gehoert zum BEREICH: sie gilt fuer alle seine Felder,
 * sie steht auch im Text von Kap. 5, und sie bleibt richtig, wenn nie jemand
 * einen Schnitt zeichnet.
 *
 * ═══ WARUM „—" UND NICHT 0 ════════════════════════════════════════════════
 * Leer bedeutet „nicht entschieden" und wird auch so gedruckt. Ein
 * Ueberaushub von 0,00 m ist die Aussage „es wird nicht ueberbaggert" — ein
 * leeres Feld ist eine offene Frage. Die Zeichnung unterscheidet beides.
 */
const AushubFelder: React.FC<{
    umgebung: Record<string, unknown>;
    klassenOptionen: { wert: string; label: string }[];
    gesperrt: boolean;
    onAendern: (u: Partial<AushubParameter>) => void;
}> = ({ umgebung, klassenOptionen, gesperrt, onAendern }) => {
    const a = leseAushub(umgebung);
    const jaNein: { wert: 'ja' | 'nein'; label: string }[] = [
        { wert: 'ja', label: 'ja' },
        { wert: 'nein', label: 'nein' },
    ];
    const alsWort = (w: boolean | null): 'ja' | 'nein' | '' =>
        (w === null ? '' : w ? 'ja' : 'nein');
    const alsWert = (w: string): boolean | null =>
        (w === 'ja' ? true : w === 'nein' ? false : null);

    return (
        <>
            <Untertitel
                text="Aushub und Sicherung"
                hinweis="Geht in den Schichtenschnitt ein. Leer heißt „nicht entschieden“ — und wird auch so gedruckt."
            />
            <div className="grid sm:grid-cols-4 gap-2 mb-3">
                <ZahlFeld
                    label="Aushubsohle"
                    wert={a.sohle_m}
                    einheit="m u. GOK"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ sohle_m: v })}
                />
                <ZahlFeld
                    label="Überaushub"
                    wert={a.ueberaushub_m}
                    einheit="m"
                    platzhalter="0,25"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ ueberaushub_m: v })}
                />
                <AuswahlFeld
                    label="Trennlage (Vlies + Folie)"
                    wert={alsWort(a.trennlage)}
                    optionen={jaNein}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ trennlage: alsWert(v) })}
                />
                <ZahlFeld
                    label="Abdeckung unbelastet"
                    wert={a.abdeckung_m}
                    einheit="m"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ abdeckung_m: v })}
                />
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mb-4">
                <AuswahlFeld
                    label="Weißbereich nach Abschluss?"
                    wert={alsWort(a.weissbereich_danach)}
                    optionen={jaNein}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ weissbereich_danach: alsWert(v) })}
                />
                <AuswahlFeld
                    label="Schutzklasse danach"
                    wert={a.schutzklasse_danach}
                    optionen={klassenOptionen}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ schutzklasse_danach: v })}
                />
            </div>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════

const TaetigkeitsZeile: React.FC<{
    taetigkeit: Taetigkeit;
    klassenOptionen: { wert: string; label: string }[];
    gesperrt: boolean;
    onAendern: (u: Partial<Taetigkeit>) => void;
    onLoeschen: () => void;
}> = ({ taetigkeit: t, klassenOptionen, gesperrt, onAendern, onLoeschen }) => {
    const wegStand = (weg: Expositionsweg) => t.expositionswege.find(w => w.weg === weg);

    const wegSetzen = (weg: Expositionsweg, relevant: boolean | null) => {
        const ohne = t.expositionswege.filter(w => w.weg !== weg);
        if (relevant === null) {
            onAendern({ expositionswege: ohne });
            return;
        }
        const alt = wegStand(weg);
        onAendern({
            expositionswege: [...ohne, { weg, relevant, begruendung: alt?.begruendung ?? '' }],
        });
    };

    return (
        <ZeilenKarte
            gesperrt={gesperrt}
            onLoeschen={onLoeschen}
            loeschTitel="Tätigkeit löschen"
            kopf={<span className="text-sm font-bold text-slate-700">{t.taetigkeit || 'Tätigkeit'}</span>}
        >
            <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <TextFeld
                    label="Tätigkeit"
                    wert={t.taetigkeit}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ taetigkeit: v })}
                />
                <TextFeld
                    label="Arbeitsverfahren"
                    wert={t.arbeitsverfahren}
                    platzhalter="Bagger mit geschlossener Kabine, Schutzbelüftung H13"
                    gesperrt={gesperrt}
                    hinweis="Das Verfahren bestimmt die Gefährdung — nicht der Stoff allein."
                    onSpeichern={v => onAendern({ arbeitsverfahren: v })}
                />
            </div>

            <div className="grid sm:grid-cols-4 gap-2 mb-2">
                <TextFeld
                    label="Wer"
                    wert={t.personenkreis}
                    platzhalter="Baggerfahrer"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ personenkreis: v })}
                />
                <ZahlFeld
                    label="Dauer je Einsatz"
                    wert={t.dauer_min}
                    einheit="min"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ dauer_min: v })}
                />
                <TextFeld
                    label="Häufigkeit"
                    wert={t.haeufigkeit}
                    platzhalter="täglich"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ haeufigkeit: v })}
                />
                <AuswahlFeld
                    label="Schutzklasse"
                    wert={t.schutzklasse_key}
                    optionen={klassenOptionen}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ schutzklasse_key: v })}
                />
            </div>

            <Untertitel
                text="Expositionswege"
                hinweis="Auch „besteht nicht“ ist ein Ergebnis — und die Prüfung, die am häufigsten fehlt."
            />
            <div className="space-y-1.5 mb-2">
                {WEGE.map(weg => {
                    const stand = wegStand(weg);
                    return (
                        <div key={weg} className="flex flex-wrap items-center gap-2">
                            <span className="w-20 text-xs font-semibold text-slate-700">{weg}</span>
                            <div className="flex gap-1">
                                {[
                                    { label: 'ja', wert: true },
                                    { label: 'nein', wert: false },
                                ].map(o => (
                                    <button
                                        key={o.label}
                                        type="button"
                                        disabled={gesperrt}
                                        onClick={() => wegSetzen(weg, stand?.relevant === o.wert ? null : o.wert)}
                                        className={`px-2 py-0.5 rounded text-xs font-bold border ${
                                            stand?.relevant === o.wert
                                                ? (o.wert ? 'bg-amber-100 border-amber-300 text-amber-900'
                                                    : 'bg-emerald-50 border-emerald-300 text-emerald-800')
                                                : 'border-slate-300 text-slate-500'
                                        }`}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {stand && (
                                <span className="flex-1 min-w-[12rem]">
                                    <TextFeld
                                        wert={stand.begruendung}
                                        platzhalter="Begründung"
                                        gesperrt={gesperrt}
                                        onSpeichern={v => onAendern({
                                            expositionswege: t.expositionswege.map(w =>
                                                w.weg === weg ? { ...w, begruendung: v } : w),
                                        })}
                                    />
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
                <AuswahlFeld
                    label="Expositionsabschätzung"
                    wert={t.exposition ?? ''}
                    optionen={STUFEN}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ exposition: v })}
                />
                <TextFeld
                    label="Begründung der Einschätzung"
                    wert={t.exposition_begruendung}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ exposition_begruendung: v })}
                />
            </div>
        </ZeilenKarte>
    );
};
